import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNewspaper, faLock } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import ReservationItems from "./Reservations/ReservationItems";
import { DefaultDateSmallMins } from "./DefaultImports";
import { CustomModal } from "./CustomModal";
import { ModernButton } from "./DefaultImports";
import { useNavigate } from "react-router-dom";

// Colors
const COLOR_GRAY = "#99B0AD";
const COLOR_LIGHT = "#FAF6E9";
const ORANGE = "#EE754C";
const BLOCKED_ACTIVITY_PLANS = [0, 1, 2, 4, 5];
const HistoryModalContent = styled.div`
  width: 100%;
`;
const HistoryBox = styled.div`
  background: white;
  border: 1px solid ${COLOR_GRAY};
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 10px;
`;
const HistoryHeader = styled.div`
  background: #f59878;
  color: ${COLOR_LIGHT};
  padding: 6px 10px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 6px 6px 0 0;
`;
const EmptyState = styled.div`
  color: ${COLOR_GRAY};
  font-size: 14px;
  text-align: center;
  padding: 28px 10px;
`;
const LockIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${ORANGE};
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(238, 117, 76, 0.12);
  }

  svg {
    filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.85));
    font-size: 24px;
  }
`;
const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${COLOR_LIGHT};
  font-size: 26px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;
const UpgradeModalContent = styled.div`
  padding: 8px 2px;
`;
const UpgradeModalText = styled.p`
  margin: 0 0 18px;
  color: #343a38;
`;
const HighlightedText = styled.span`
  color: ${ORANGE};
  font-weight: 600;
`;
const UpgradeModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
`;
const normalizeValue = value => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};
const normalizeDate = value => {
  if (!value) return "";
  return String(value).slice(0, 10);
};
const normalizeTime = value => {
  if (!value) return "";
  return String(value).slice(0, 5);
};
const normalizeNumber = value => {
  if (value === undefined || value === null || value === "") return "";
  const number = Number(value);
  return Number.isNaN(number) ? String(value).trim() : String(number);
};
const getIdValue = value => {
  if (value && typeof value === "object") {
    return value.id ?? value.pk ?? "";
  }
  return value ?? "";
};
const isNonZero = value => {
  const number = Number(value);
  return !Number.isNaN(number) && number !== 0;
};
const isCancelledStatus = status => {
  return ["Cancelled", "cancelled", "cancelled_customer", "cancelled_restaurant"].includes(status);
};
const formatPhoneNumber = (phone, countryCode) => {
  if (!phone) return "";
  if (!countryCode) return String(phone).trim();
  const cleanPhone = String(phone).replace(/\s/g, "");
  const cleanCode = String(countryCode).replace(/\D/g, "");
  if (!cleanCode) return cleanPhone;
  if (cleanPhone.startsWith(cleanCode)) {
    return `+${cleanCode} ${cleanPhone.slice(cleanCode.length)}`.trim();
  }
  return `+${cleanCode} ${cleanPhone}`.trim();
};
const calcConfigSum = (numOfTables, configList = []) => {
  if (!Array.isArray(configList) || numOfTables < 2) return 0;
  const chairsByTableCount = configList.reduce((acc, item) => {
    const tableCount = Number(item?.number_of_tables);
    const chairs = Number(item?.number_of_chairs) || 0;
    if (!Number.isNaN(tableCount)) {
      acc[tableCount] = chairs;
    }
    return acc;
  }, {});
  let configSum = 0;
  if (numOfTables >= 2) configSum += chairsByTableCount[2] || 0;
  if (numOfTables >= 3) configSum += chairsByTableCount[3] || 0;
  if (numOfTables >= 4) configSum += chairsByTableCount[4] || 0;
  for (let i = 5; i <= numOfTables; i++) {
    configSum += chairsByTableCount[(i + 1) % 2 + 1] || 0;
  }
  return configSum;
};
const parseTableIdsFromString = tablesString => {
  if (!tablesString) return [];
  return String(tablesString).split(",").map(n => parseInt(n, 10)).filter(n => !Number.isNaN(n)).sort((a, b) => a - b);
};
const getTablesSummaryLabel = (tablesString, allTables, configList) => {
  if (!tablesString || !Array.isArray(allTables)) return "";
  const tableIds = parseTableIdsFromString(tablesString);
  if (tableIds.length === 0) return "";
  const filteredTables = allTables.filter(table => tableIds.includes(table.id));
  const baseSeats = filteredTables.reduce((acc, table) => acc + (Number.parseInt(table.number_of_seats, 10) || 0), 0);
  const tableCount = filteredTables.length;
  const configSum = calcConfigSum(tableCount, configList);
  const totalChairs = baseSeats + configSum;
  return `${tableCount} Mesas / ${totalChairs} Cadeiras`;
};
const getZoneLabel = (zoneId, zonesWithId) => {
  const id = getIdValue(zoneId);
  if (id === "") return "Sala não definida";
  return zonesWithId?.[id] ?? String(id);
};
const valuesChanged = (previousValue, currentValue) => {
  return normalizeValue(previousValue) !== normalizeValue(currentValue);
};
const isCreatedHistoryRecord = ({
  reservation,
  previousReservation,
  index,
  historyLength
}) => {
  if (!reservation) return false;
  const historyType = String(reservation.history_type || "").trim();
  const action = String(reservation.action || reservation.type || reservation.history_action || reservation.history_change_reason || "").trim().toLowerCase();
  if (historyType === "+") return true;
  if (action === "created" || action === "create" || action === "criado") {
    return true;
  }

  // Most history arrays are newest -> oldest.
  // The oldest entry has no previous record, so it represents the original creation.
  if (!previousReservation && index === historyLength - 1) {
    return true;
  }

  // If there is only one history entry, it should still be visible as the creation record.
  if (!previousReservation && historyLength === 1) {
    return true;
  }
  return false;
};
const hasVisibleReservationChange = ({
  reservation,
  previousReservation,
  allTables,
  tablesConfigList,
  zonesWithId
}) => {
  if (!reservation || !previousReservation) return false;
  const checks = [];

  // Nome
  checks.push([previousReservation.full_name, reservation.full_name]);

  // Telefone confirmado
  checks.push([previousReservation.is_phone_confirmed, reservation.is_phone_confirmed]);

  // Telemóvel visível com código do país
  checks.push([formatPhoneNumber(previousReservation.phone_number, previousReservation.country_code), formatPhoneNumber(reservation.phone_number, reservation.country_code)]);

  // Data
  checks.push([normalizeDate(previousReservation.date), normalizeDate(reservation.date)]);

  // Hora
  checks.push([normalizeTime(previousReservation.time), normalizeTime(reservation.time)]);

  // Nº de pessoas
  checks.push([normalizeNumber(previousReservation.number_of_people), normalizeNumber(reservation.number_of_people)]);

  // Cadeira de bebé
  if (isNonZero(previousReservation.baby_chair) || isNonZero(reservation.baby_chair)) {
    checks.push([normalizeNumber(previousReservation.baby_chair), normalizeNumber(reservation.baby_chair)]);
  }

  // Zona
  checks.push([getZoneLabel(previousReservation.table_place_preference, zonesWithId), getZoneLabel(reservation.table_place_preference, zonesWithId)]);

  // Mesas usadas
  const shouldCompareTables = Array.isArray(allTables) && !isCancelledStatus(reservation.status) && (previousReservation.tablesused || reservation.tablesused);
  if (shouldCompareTables) {
    checks.push([getTablesSummaryLabel(previousReservation.tablesused, allTables, tablesConfigList), getTablesSummaryLabel(reservation.tablesused, allTables, tablesConfigList)]);
  }

  // Mesas insuficientes
  checks.push([previousReservation.not_enough_seats, reservation.not_enough_seats]);

  // Estado
  checks.push([previousReservation.status, reservation.status]);

  // Acessibilidade
  if (previousReservation.accessability === "wheel_chair" || reservation.accessability === "wheel_chair") {
    checks.push([previousReservation.accessability === "wheel_chair" ? "Mob. Red." : "Nenhuma", reservation.accessability === "wheel_chair" ? "Mob. Red." : "Nenhuma"]);
  }
  return checks.some(([previousValue, currentValue]) => valuesChanged(previousValue, currentValue));
};
const getChangeAuthorLabel = changesMadeBy => {
  const value = changesMadeBy?.toLowerCase();
  if (value === "system") return "Restaurante";
  if (value === "customer") return "Cliente";
  if (value === "restaurant") return "Restaurante";
  return changesMadeBy || "Sistema";
};
const ReservationHistory = ({
  reservationHistory,
  timezone,
  all_tables,
  zones_with_id,
  isActivityBlocked = false,
  restaurantId = null
}) => {
  const {
    t
  } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();
  const tablesConfigList = useSelector(state => state.connectTablesConfig?.connectTablesConfig || []);
  const visibleHistory = useMemo(() => {
    if (!Array.isArray(reservationHistory)) return [];
    const historyLength = reservationHistory.length;
    return reservationHistory.map((reservation, index) => {
      const previousReservation = reservationHistory[index + 1] || null;
      const isCreatedRecord = isCreatedHistoryRecord({
        reservation,
        previousReservation,
        index,
        historyLength
      });
      return {
        reservation,
        previousReservation,
        isCreatedRecord
      };
    }).filter(({
      reservation,
      previousReservation,
      isCreatedRecord
    }) => {
      if (isCreatedRecord) return true;
      return hasVisibleReservationChange({
        reservation,
        previousReservation,
        allTables: all_tables,
        tablesConfigList,
        zonesWithId: zones_with_id
      });
    });
  }, [reservationHistory, all_tables, tablesConfigList, zones_with_id]);
  const handleIconClick = () => {
    if (isActivityBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    setShowModal(true);
  };
  const handleViewPlans = () => {
    setShowUpgradeModal(false);
    if (restaurantId) {
      navigate(`/pick_plan_and_pay/${restaurantId}`);
    }
  };
  return <>
      {isActivityBlocked ? <LockIconWrapper role="button" tabIndex={0} title={t("ui.components.reservationHistory.registoDeAtividadeDisponivelNosPlanos")} aria-label={t("ui.components.reservationHistory.atualizarOPlanoParaAcederAo")} onClick={handleIconClick} onKeyDown={event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleIconClick();
      }
    }}>
          <FontAwesomeIcon icon={faNewspaper} style={{
        color: ORANGE
      }} />
        </LockIconWrapper> : <IconWrapper role="button" tabIndex={0} onClick={handleIconClick} onKeyDown={event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleIconClick();
      }
    }}>
          <FontAwesomeIcon icon={faNewspaper} />
        </IconWrapper>}

      <CustomModal show={showModal} onHide={() => setShowModal(false)} title={t("ui.components.reservationHistory.registoDeAtividade")} centered>
        <HistoryModalContent>
          {visibleHistory.length === 0 ? <EmptyState>{t("ui.components.reservationHistory.semAlteracoesRelevantesParaMostrar")}</EmptyState> : visibleHistory.map(({
          reservation,
          previousReservation,
          isCreatedRecord
        }, index) => <HistoryBox key={reservation.history_id || `${reservation.id}-${reservation.history_date}-${index}`}>
                  <HistoryHeader>
                    <Row>
                      <Col xs={4}>
                        {isCreatedRecord ? "Criação" : `Atualização #${visibleHistory.length - index}`}
                      </Col>

                      <Col xs={4} style={{
                textTransform: "capitalize"
              }}>
                        {getChangeAuthorLabel(reservation.changes_made_by)}
                      </Col>

                      <Col xs={4}>
                        <DefaultDateSmallMins date={reservation.history_date} />
                      </Col>
                    </Row>
                  </HistoryHeader>

                  <div style={{
            padding: "8px 4px"
          }}>
                    <ReservationItems previousReservationFromHistory={isCreatedRecord ? reservation : previousReservation} reservation={reservation} all_tables={all_tables} isReservationHistory={true} isReservationFromRestaurantScreen={isCreatedRecord} />
                  </div>
                </HistoryBox>)}
        </HistoryModalContent>
      </CustomModal>

      <CustomModal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} title={t("ui.components.reservationHistory.registoDeAtividade2")}>
        <UpgradeModalContent>
          <UpgradeModalText>{t("ui.components.reservationHistory.o")}<HighlightedText>{t("ui.components.reservationHistory.registoDeAtividade3")}</HighlightedText>{t("ui.components.reservationHistory.estaDisponivelNosPlanosSuperioresAtualiza")}</UpgradeModalText>

          <UpgradeModalButtons>
            <ModernButton type="button" color="#99b0ad" onClick={() => setShowUpgradeModal(false)}>{t("ui.components.reservationHistory.agoraNao")}</ModernButton>

            <ModernButton type="button" color="#ee754c" onClick={handleViewPlans}>{t("ui.components.reservationHistory.verPlanos")}</ModernButton>
          </UpgradeModalButtons>
        </UpgradeModalContent>
      </CustomModal>
    </>;
};
export default ReservationHistory;
